import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Text from 'components/text';
import StatChip from 'components/stat-chip';
import {
  Grid,
  Box,
  MenuItem,
  CircularProgress,
  // Select,
  // FilledInput,
  // Chip,
  // Checkbox,
  // InputBase,
  debounce,
  BoxProps,
} from '@mui/material';
import BaseInput from 'components/inputs/base-input';
import AutocompleteInput, { IOption } from 'components/inputs/autocomplete-input';
import StreamsStackedBar from './StreamsStackedBar';
import StreamsByTrack from './StreamsByTrack';
// import { ReactComponent as TiltArrowIcon } from 'assets/icons/tilt_arrow.svg';
import ComparableTracksList from './ComparableTracksList';
import api from 'utils/api';
import { getColorsByPlatform, assignLineColors, getNextLineColor } from 'utils/getColorsRange';
import MultiSelect, { MultiSelectOptionProps } from 'components/inputs/multi-select';
import { useUserData } from 'hooks/useUserData';
import Loading from './Loading';
import { useTranslation } from 'react-i18next';
// import { createAxiosCancelToken } from '../../../../utils/axios.instance';
// import axios, { CancelTokenSource } from 'axios';
// import useCancelToken from '../../../../hooks/useCancelToken';

export interface AnalyticsTrack {
  trackId: string;
  label: string;
  streams: number;
  visible: boolean;
  color: string;
  data: {
    x: string;
    y: number;
  }[];
}

export interface AnalyticsCountry {
  code: string;
  name: string;
  streams: number;
}

export interface AnalyticsPlatform {
  id: number;
  dspName: string;
  fugaDspId: string;
}

// interface AnalyticsEngagementByDay {
//   totallisteners: string;
//   totalstreams: string;
//   totalsaves: string;
// }

interface AnalyticsEngagementByEvents {
  value: number;
  absoluteDifference: number;
  percentageDifference: number;
}

export interface AnalyticsEngagement {
  firstEngagement?: number;
  lastEngagement?: number;
  // @reference: Take a look at AnalyticsService in backend code. Disabled for now.
  // firstEngagement?: AnalyticsEngagementByDay;
  // lastEngagement?: AnalyticsEngagementByDay;
  listeners: AnalyticsEngagementByEvents;
  streams: AnalyticsEngagementByEvents;
  saves: AnalyticsEngagementByEvents;
  streamsPerListener: AnalyticsEngagementByEvents;
}

interface StreamsByPlatformDspData {
  dspName: string;
  dspFugaId: string;
  totalStreams: number;
}

interface StreamsByPlatformStackedBarData {
  [key: string]: string | number;
}

export interface IStreamsByPlatform {
  dspData: StreamsByPlatformDspData[];
  stackedBarData: StreamsByPlatformStackedBarData[];
  // totalStreams: number;
  totalStreamsPercentageChange: number;
}

interface DataPoint {
  x: string;
  y: number;
}

export interface IStreamsByTrack {
  trackId: string;
  label: string;
  streams: number;
  color?: string;
  visible?: boolean;
  data: DataPoint[];
}

export interface IArtistProfile {
  artistSpotifyId: string;
  artistName: string;
}

// Active analytics filters shared with the track search (Platforms / Date range / Reports source).
export interface AnalyticsFilters {
  dsp: string;
  dateRange: string;
  country: string;
}

interface BarChipProps extends BoxProps {
  label: string;
  color: string;
  value: string;
}

const BarChip = ({ label, color, value, ...rest }: BarChipProps) => {
  return (
    <Box {...rest}>
      <Box
        width="24px"
        height="4px"
        borderRadius={6}
        marginBottom={1}
        sx={{ backgroundColor: color }}
      />
      <Box paddingBottom={0.5}>
        <Text fontSize="16px" color="#222222" fontWeight="bold">
          {label}
        </Text>
      </Box>
      <Text fontSize="14px" color="#484848">
        {value}
      </Text>
    </Box>
  );
};

// Max tracks shown at once on the "Streams by Track" chart (default top-5 +
// user-added). Keeps the chart readable and within the color palette.
const MAX_COMPARABLE_TRACKS = 20;

const FugaAnalytics: React.FC = () => {
  const { t } = useTranslation();
  // const [topArtistProfiles, setTopArtistProfiles] = useState<IArtistProfile[] | undefined>(
  //   undefined
  // );
  const [artistProfiles, setArtistProfiles] = useState<IArtistProfile[]>([]);
  const [countries, setCountries] = useState<AnalyticsCountry[]>([]);
  const [engagements, setEngagements] = useState<AnalyticsEngagement | null>(null);
  const [streamsByPlatform, setStreamsByPlatform] = useState<IStreamsByPlatform | null>(null);
  const [streamsByTrack, setStreamsByTrack] = useState<IStreamsByTrack[]>([]);
  // User-added ("pinned") track IDs shown on top of the default top-5. Held in a
  // ref too so the filter-fetch effect can read the latest set without re-running
  // when the user adds/removes a track (adding fetches just the new line).
  const [customTrackIds, setCustomTrackIds] = useState<string[]>([]);
  const customTrackIdsRef = useRef<string[]>([]);
  // Whether the artist has any chartable tracks (from the latest fetch). Distinct
  // from `streamsByTrack.length` so emptying the list (deleting all tracks) keeps
  // the chart + search visible instead of collapsing to "No data available".
  const [artistHasTracks, setArtistHasTracks] = useState(false);
  const [loadingEngagement, setLoadingEngagement] = useState(false);
  const [loadingStreamsByPlatform, setLoadingStreamsByPlatform] = useState(false);
  const [loadingStreamsByTrack, setLoadingStreamsByTrack] = useState(false);

  const [selectedSpProfile, setSelectedSpProfile] = useState<string | null>(null);
  const [artistOptions, setArtistOptions] = useState<IOption[]>([]);
  const [selectedArtistOption, setSelectedArtistOption] = useState<IOption | null>(null);

  const [filterDsp, setFilterDsp] = useState<string>('all');
  const [filterDateRange, setFilterDateRange] = useState<string>('last-three-months');
  const [filterCountry, setFilterCountry] = useState<string>('world');

  // Stable identity so passing it down doesn't spuriously re-trigger the track search fetch.
  const trackFilters = useMemo(
    () => ({ dsp: filterDsp, dateRange: filterDateRange, country: filterCountry }),
    [filterDsp, filterDateRange, filterCountry]
  );

  const [platformOptions, setPlatformOptions] = useState<MultiSelectOptionProps[]>([]);

  const [loading, setLoading] = useState(true);

  const [pageContainerWidth, setPageContainerWidth] = useState(0);
  const pageContainer = useRef<HTMLDivElement>(null);

  const selectPlatforms = useCallback(async (value: MultiSelectOptionProps[]) => {
    const selectedPlatforms: string = value.length ? value.map((v) => v.value).join(',') : 'all';
    setFilterDsp(selectedPlatforms);
  }, []);

  const handleSelectPlatforms = debounce(selectPlatforms, 1500);
  const { permissionsData } = useUserData();

  // const { cancelableRun } = useCancelToken();

  useEffect(() => {
    if (artistProfiles) {
      const options = artistProfiles.map((profile) => ({
        value: profile.artistSpotifyId,
        label: profile.artistName,
      }));
      setArtistOptions(options);
    }
  }, [artistProfiles]);

  useEffect(() => {
    const option = artistOptions.find((opt) => opt.value === selectedSpProfile) || null;
    setSelectedArtistOption(option);
  }, [artistOptions, selectedSpProfile]);

  useEffect(() => {
    const updateWidth = () => {
      if (pageContainer.current) {
        setPageContainerWidth(pageContainer.current.offsetWidth);
        // Without the following timeout, the width is not updated correctly due to the dynamic sidebar
        setTimeout(() => {
          if (pageContainer.current) {
            setPageContainerWidth(pageContainer?.current?.offsetWidth);
          }
        }, 0);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const getFilteredTracks = () => {
    return streamsByTrack.filter((track) => track.visible);
  };

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setLoading(true);
      try {
        const getAllPlatforms: AnalyticsPlatform[] = await api.analytics.getAllPlatforms();

        if (isMounted) {
          setPlatformOptions(
            getAllPlatforms.map((pf) => ({ label: pf.dspName, value: pf.fugaDspId }))
          );
        }

        const profiles: IArtistProfile[] = await api.analytics.getArtistProfiles();
        if (isMounted) {
          // setTopArtistProfiles(profiles);
          setArtistProfiles(profiles);
        }

        const [selectedProfileData] = profiles;
        if (selectedProfileData && isMounted) {
          setSelectedSpProfile(selectedProfileData.artistSpotifyId);
        }
      } catch (error) {
        console.log(error);
      }
      setLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      if (selectedSpProfile) {
        const getTopCountries: AnalyticsCountry[] = await api.analytics.getTopCountries({
          params: {
            spProfile: selectedSpProfile,
          },
        });
        if (isMounted) {
          setCountries(getTopCountries);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [selectedSpProfile]);

  // Pinned tracks belong to a specific artist — drop them when the artist changes.
  // Declared before the fetch effect so the ref is cleared before that effect reads it.
  useEffect(() => {
    setCustomTrackIds([]);
    customTrackIdsRef.current = [];
  }, [selectedSpProfile]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      if (selectedSpProfile) {
        setLoadingEngagement(true);
        setLoadingStreamsByPlatform(true);
        setLoadingStreamsByTrack(true);
        const filters = {
          params: {
            dsp: filterDsp,
            dateRange: filterDateRange,
            country: filterCountry,
            spProfile: selectedSpProfile,
          },
        };

        api.analytics.getEngagement(filters).then((response) => {
          if (isMounted) {
            // @ts-ignore
            setEngagements(response);
            setLoadingEngagement(false);
          }
        });

        api.analytics.getStreamsByPlatform(filters).then((response) => {
          if (isMounted) {
            // @ts-ignore
            setStreamsByPlatform(response);
            setLoadingStreamsByPlatform(false);
          }
        });

        // Default top-5 (recomputed for current filters) merged with the user's
        // pinned tracks, so added tracks persist and re-sync when filters change.
        const customIds = customTrackIdsRef.current;
        Promise.all([
          api.analytics.getStreamsByTrack(filters),
          customIds.length
            ? api.analytics.getStreamsByTrack({
                params: { ...filters.params, spTrackIds: customIds.join(',') },
              })
            : Promise.resolve([]),
        ]).then(([defaultTracks, customTracks]: [any, any]) => {
          if (isMounted) {
            const byId = new Map<string, any>();
            [...(defaultTracks || []), ...(customTracks || [])].forEach((track) => {
              if (!byId.has(track.trackId)) byId.set(track.trackId, track);
            });
            setArtistHasTracks(byId.size > 0);
            setStreamsByTrack(
              assignLineColors(Array.from(byId.values())).map((track) => ({
                ...track,
                visible: true,
              }))
            );
            setLoadingStreamsByTrack(false);
          }
        });
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [selectedSpProfile, filterDsp, filterDateRange, filterCountry]);

  // Stable identities (functional setState + refs only) so ComparableTracksList
  // can memoize its rendered rows against these handlers.
  const toggleTrackVisibility = useCallback((trackId: string) => {
    setStreamsByTrack((prevTracks) => {
      return prevTracks.map((track) => {
        if (track.trackId === trackId) {
          track.visible = !track.visible;
        }
        return track;
      });
    });
  }, []);

  const removeTrack = useCallback((trackId: string) => {
    setStreamsByTrack((prevTracks) => {
      return prevTracks.filter((track) => track.trackId !== trackId);
    });
    // Also unpin it so a removed track doesn't reappear on the next refetch.
    customTrackIdsRef.current = customTrackIdsRef.current.filter((id) => id !== trackId);
    setCustomTrackIds((prevIds) => prevIds.filter((id) => id !== trackId));
  }, []);

  const addTrack = async (trackId: string) => {
    if (
      !selectedSpProfile ||
      streamsByTrack.some((track) => track.trackId === trackId) ||
      streamsByTrack.length >= MAX_COMPARABLE_TRACKS
    ) {
      return;
    }
    // Fetch only the new line so existing tracks (and their visibility) are untouched.
    const response: IStreamsByTrack[] = await api.analytics.getStreamsByTrack({
      params: {
        dsp: filterDsp,
        dateRange: filterDateRange,
        country: filterCountry,
        spProfile: selectedSpProfile,
        spTrackIds: trackId,
      },
    });
    customTrackIdsRef.current = [...customTrackIdsRef.current, trackId];
    setCustomTrackIds((prevIds) => [...prevIds, trackId]);
    setStreamsByTrack((prevTracks) => {
      const usedColors = prevTracks.map((track) => track.color).filter(Boolean) as string[];
      const additions = (response || [])
        .filter((track) => !prevTracks.some((existing) => existing.trackId === track.trackId))
        .map((track) => {
          const color = getNextLineColor(usedColors);
          usedColors.push(color);
          return { ...track, color, visible: true };
        });
      return [...prevTracks, ...additions];
    });
  };

  const formatChanges = (value: number | undefined) => {
    return (value || 0) > 0
      ? `+${(value || 0).toLocaleString()}`
      : `${(value || 0).toLocaleString()}`;
  };

  if (!permissionsData?.analytics) {
    return (
      <Box padding={3}>
        <Text>
          {t(
            'Sorry, the page you are looking for does not exist, please contact with us if you are facing an issues.'
          )}
        </Text>
      </Box>
    );
  }

  return (
    <Grid container flexDirection="column" padding={2} marginBottom={10}>
      <>
        <Box
          ref={pageContainer}
          display="flex"
          flexDirection="row"
          alignItems="center"
          marginBottom={2}
        >
          {/* <Box display="flex" gap={2} sx={{ flexGrow: 1, flexBasis: 3 }}> */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box flex={1}>
                    <AutocompleteInput
                      matchFrom="any"
                      size="small"
                      dark={false}
                      disabled={loading}
                      name="spotifyId"
                      error={loading ? false : selectedSpProfile === null}
                      onChange={(value) => {
                        setSelectedSpProfile(value);
                      }}
                      disableClearable={true}
                      value={selectedArtistOption}
                      options={artistOptions}
                      label={t('Artist')}
                      selectOnFocus
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box flex={1}>
                    <MultiSelect
                      label={t('Platforms')}
                      options={platformOptions}
                      defaultValue={t('All DSPs')}
                      onSelect={handleSelectPlatforms}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box flex={1}>
                    <BaseInput
                      disabled={loading}
                      size="small"
                      select
                      variant="filled"
                      name="distributor"
                      label={t('Date Range')}
                      defaultValue={filterDateRange}
                      SelectProps={{ displayEmpty: true }}
                      onChange={(event: any) => {
                        setFilterDateRange(event.target.value);
                      }}
                      error={false}
                      sx={{ width: '100%' }}
                    >
                      <MenuItem value="last-week">{t('Last Week')}</MenuItem>
                      <MenuItem value="last-month">{t('Last Month')}</MenuItem>
                      <MenuItem value="last-two-months">{t('Last Two Months')}</MenuItem>
                      <MenuItem value="last-three-months">{t('Last Three Months')}</MenuItem>
                      <MenuItem value="last-six-months">{t('Last Six Months')}</MenuItem>
                    </BaseInput>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box flex={1}>
                    <BaseInput
                      disabled={loading}
                      size="small"
                      select
                      variant="filled"
                      name="distributor"
                      label={t('Reports source')}
                      defaultValue="world"
                      SelectProps={{ displayEmpty: true }}
                      onChange={(event: any) => {
                        setFilterCountry(event.target.value);
                      }}
                      error={false}
                      sx={{ width: '100%' }}
                    >
                      <MenuItem selected={true} value="world">
                        {t('All Countries')}
                      </MenuItem>
                      {countries.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </BaseInput>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        {/* </Box> */}
      </>

      {loadingEngagement ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <StatChip label="Listeners" value={0} growth="-" changes="-" hideGrowth={true} />
            <StatChip label="Streams" value={0} growth="-" changes="-" hideGrowth={true} />
          </Grid>
          <Grid item xs={12} md={6}>
            <StatChip
              label={t('Streams / Listener')}
              value={0}
              growth="-"
              changes="-"
              hideGrowth={true}
            />
            <StatChip label={t('Saves')} value={0} growth="-" changes="-" hideGrowth={true} />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {/* <Box display="flex" gap={2}> */}
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2}>
              <StatChip
                label={t('Listeners')}
                value={engagements?.listeners.value}
                growth={`${engagements?.listeners.percentageDifference}%`}
                changes={formatChanges(engagements?.listeners.absoluteDifference)}
                hideGrowth={true}
              />
              <StatChip
                label={t('Streams')}
                value={engagements?.streams.value}
                growth={`${engagements?.streams.percentageDifference}%`}
                changes={formatChanges(engagements?.streams.absoluteDifference)}
                hideGrowth={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2}>
              <StatChip
                label={t('Streams / Listener')}
                value={engagements?.streamsPerListener.value}
                growth={`${engagements?.streamsPerListener.percentageDifference}%`}
                changes={formatChanges(engagements?.streamsPerListener.absoluteDifference)}
                hideGrowth={true}
              />
              <StatChip
                label={t('Saves')}
                value={engagements?.saves.value}
                growth={`${engagements?.saves.percentageDifference}%`}
                changes={formatChanges(engagements?.saves.absoluteDifference)}
                hideGrowth={true}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {loading && <Loading />}

      <Box
        marginTop={2}
        borderRadius={2}
        padding={2}
        border={1}
        borderColor="#DFE1E6"
        maxWidth="100%"
        overflow="hidden"
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={2}
        >
          <Text sx={{ fontSize: 20, fontWeight: 'bold' }}>{t('Streams by Platform')}</Text>
        </Box>

        {loadingStreamsByPlatform ? (
          <Box display="flex" flexDirection="row" gap={1}>
            <CircularProgress size={18} sx={{ color: '#333' }} />
            <Text>{t('Processing top DSP data and their streams.')}</Text>
          </Box>
        ) : (
          <>
            {streamsByPlatform?.dspData.length === 0 ? (
              <Box>
                <Text>{t('No data available')}</Text>
              </Box>
            ) : (
              <>
                {/* <Box marginBottom="32px">
                  <Text fontSize="16px" color="#484848" paddingBottom="3px">
                    Total Streams
                  </Text>
                  <Box display="flex" flexDirection="row" gap={1.2}>
                    <Text fontSize="24px" fontWeight="bold" alignSelf="end">
                      {(streamsByPlatform?.totalStreams || 0).toLocaleString()}
                    </Text>
                    <Box display="flex" alignItems="end">
                      <Text fontSize="14px" fontWeight="bold" lineHeight={1.3}>
                        {streamsByPlatform?.totalStreamsPercentageChange}%
                      </Text>
                      <Box paddingLeft={0.6}>
                        <TiltArrowIcon width={8.5} height={8.5} />
                      </Box>
                    </Box>
                  </Box>
                </Box> */}

                <Box
                  marginTop="40px"
                  marginBottom="20px"
                  display="flex"
                  minWidth={0}
                  paddingRight={2}
                  gap={2}
                  justifyContent="space-between"
                  sx={{
                    overflowY: 'hidden',
                    overflowX: 'auto',
                  }}
                >
                  {streamsByPlatform?.dspData.map((dsp, index) => (
                    <Box key={dsp.dspFugaId} flex="0 0 auto">
                      <BarChip
                        label={dsp.dspName}
                        color={getColorsByPlatform[dsp.dspName]}
                        value={(dsp?.totalStreams || 0).toLocaleString()}
                      />
                    </Box>
                  ))}
                </Box>

                <Box>
                  {(streamsByPlatform?.stackedBarData || []).length > 0 ? (
                    <StreamsStackedBar
                      width={pageContainerWidth - 0}
                      platformStreams={streamsByPlatform?.stackedBarData || []}
                    />
                  ) : (
                    <Text>{t('No data available')}</Text>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      <Box marginTop={2} borderRadius={2} padding={2} border={1} borderColor="#DFE1E6">
        <Grid container spacing={2}>
          {/* Left: title + chart (title stays top-left, always visible). */}
          <Grid item xs={12} lg={9}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={2}
            >
              <Text sx={{ fontSize: 20, fontWeight: 'bold' }}>{t('Streams by Track')}</Text>
            </Box>

            {loadingStreamsByTrack ? (
              <Box display="flex" flexDirection="row" gap={1}>
                <CircularProgress size={18} sx={{ color: '#333' }} />
                <Text>{t('Processing top tracks data and their streams.')}</Text>
              </Box>
            ) : !artistHasTracks ? (
              <Text>{t('No data available')}</Text>
            ) : (
              <Box>
                <StreamsByTrack
                  width={
                    pageContainerWidth > 900 ? pageContainerWidth - 290 : pageContainerWidth - 20
                  }
                  tracks={getFilteredTracks()}
                />
              </Box>
            )}
          </Grid>

          {/* Right: track list, starting from the top alongside the title. */}
          <Grid item xs={12} lg={3}>
            {!loadingStreamsByTrack && artistHasTracks && (
              <Box flexGrow={1} width={{ xs: '100%' }} flexShrink={0}>
                <ComparableTracksList
                  tracks={streamsByTrack}
                  spProfile={selectedSpProfile}
                  addTrack={addTrack}
                  removeTrack={removeTrack}
                  toggleTrackVisibility={toggleTrackVisibility}
                  maxTracks={MAX_COMPARABLE_TRACKS}
                  canAddTrack={streamsByTrack.length < MAX_COMPARABLE_TRACKS}
                  filters={trackFilters}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default FugaAnalytics;
